import { ref } from 'vue';
import { watchOnce } from '@vueuse/core';

import { exhaustiveGuard } from '@libs/utils';

import { isFeatureToggleActive } from '../customFeatureToggles';

import type { StatsigExperimentName } from './statsigFeatureToggleTypes';

const areAllExperimentsLoaded = ref<boolean>(false);

let isNewPopularitySortOrderExperiment = false;
let isShowDone = false;
let isHideBorderOnSuggestedFlight = false;
let isLandBellowHeaderInMobileExperiment = false;
const isNewButtonExperiment = ref<boolean>(false);
const isPriceGuaranteeAlternativeTitleExperiment = ref<boolean>(false);
let isMobileOverviewFilterPillsExperiment = false;
let isHotelDetailsRoomCardsGridLayoutExperiment = false;
let isNewSelectedRoomButtonExperiment = false;
let isStenaLineFerryModalExperiment = false;
let isNoTransportUpsellExperiment = false;
let isCancellationPoliciesInTripOverviewExperiment = false;
let isSummaryPaxClickabkeExperiment = false;

export function setExperimentValue(
  experimentName: StatsigExperimentName,
  value: boolean
): void {
  switch (experimentName) {
    case 'alternate_sort_order':
      isNewPopularitySortOrderExperiment = value;
      break;

    case 'new_button':
      isNewButtonExperiment.value = value;
      break;

    case 'price_guarantee_alterantive_title':
      isPriceGuaranteeAlternativeTitleExperiment.value = value;
      break;

    case 'list_pages_copy_done':
      isShowDone = value;
      break;
    case 'land_bellow_header_in_mobile':
      isLandBellowHeaderInMobileExperiment = value;
      break;

    case 'hide_border_on_suggested_flight':
      isHideBorderOnSuggestedFlight = value;
      break;

    case 'compact_mobile_filter_pills':
      isMobileOverviewFilterPillsExperiment = value;
      break;

    case 'hotel_details_room_cards_grid_layout':
      isHotelDetailsRoomCardsGridLayoutExperiment = value;
      break;

    case 'new_rooms_button':
      isNewSelectedRoomButtonExperiment = value;
      break;

    case 'open_ferry_modal_for_stena_customers':
      isStenaLineFerryModalExperiment = value;
      break;

    case 'upsell_transport_in_itinerary':
      isNoTransportUpsellExperiment = value;
      break;

    case 'cancellation_policies_in_trip_overview':
      isCancellationPoliciesInTripOverviewExperiment = value;
      break;

    case 'clickable_pax_in_summary':
      isSummaryPaxClickabkeExperiment = value;
      break;

    default:
      exhaustiveGuard(experimentName, 'Invalid experiment name');
  }
}

function getExperimentValue(experimentName: StatsigExperimentName): boolean {
  switch (experimentName) {
    case 'alternate_sort_order':
      return isNewPopularitySortOrderExperiment;

    case 'new_button':
      return isNewButtonExperiment.value;

    case 'price_guarantee_alterantive_title':
      return isPriceGuaranteeAlternativeTitleExperiment.value;

    case 'list_pages_copy_done':
      return isShowDone;

    case 'hotel_details_room_cards_grid_layout':
      return isHotelDetailsRoomCardsGridLayoutExperiment;

    case 'hide_border_on_suggested_flight':
      return isHideBorderOnSuggestedFlight;

    case 'land_bellow_header_in_mobile':
      return isLandBellowHeaderInMobileExperiment;

    case 'compact_mobile_filter_pills':
      return isMobileOverviewFilterPillsExperiment;

    case 'new_rooms_button':
      return isNewSelectedRoomButtonExperiment;

    case 'open_ferry_modal_for_stena_customers':
      return isStenaLineFerryModalExperiment;

    case 'upsell_transport_in_itinerary':
      return isNoTransportUpsellExperiment;

    case 'cancellation_policies_in_trip_overview':
      return isCancellationPoliciesInTripOverviewExperiment;

    case 'clickable_pax_in_summary':
      return isSummaryPaxClickabkeExperiment;

    default:
      exhaustiveGuard(experimentName, 'Invalid experiment name');
  }
}

export function isExperiment(experimentName: StatsigExperimentName): boolean {
  return (
    (getExperimentValue(experimentName) ||
      isFeatureToggleActive(experimentName)) &&
    !isFeatureToggleActive(`!${experimentName}`)
  );
}

export function completeExperimentsLoading(): void {
  areAllExperimentsLoaded.value = true;
}

export async function waitUntilExperimentsAreLoaded(): Promise<void> {
  return new Promise((resolve) => {
    if (areAllExperimentsLoaded.value) {
      resolve();
    } else {
      watchOnce(areAllExperimentsLoaded, async () => resolve());
    }
  });
}
